<template>
    <div>
        <md-dialog md-open-from="#deletePage" md-close-to="#deletePage" ref="deletePage" @open="fixContent" @close="startOver">
            <div style="text-align: center;">
                <div style="padding: 14px;padding-bottom: 0px;" >
                    <md-dialog-content>
                        <p>Delete Page</p>
                        <div class="from-to-wrap">
                            <div class="from-to">
                                <md-input-container>
                                    <label for="delete-page-from">From </label>
                                    <md-input id="delete-page-from" type="number" readonly v-model="delete_page_from" min="1" :max="totalPages" @blur="deleteValidateNumber"/>
                                </md-input-container>
            
                                <md-input-container>
                                    <label for="delete-page-to">To </label>
                                    <md-input id="delete-page-to" type="number" readonly v-model="delete_page_to" min="1" :max="totalPages" @blur="deleteValidateToNumber"/>
                                </md-input-container>
                            </div>
                            
                            <div class="comm-error">
                                <p v-if="deleteErrorFromMessage" class="error">{{ deleteErrorFromMessage }}</p>
                                <p v-if="deleteErrorToMessage" class="error">{{ deleteErrorToMessage }}</p>
                            </div>
                        </div>
                        <md-button @click="deletePages" class="md-warn md-raised">Delete Page(s)</md-button>     
                    </md-dialog-content>
                </div>  
            </div>
        </md-dialog>
        <md-dialog ref="savedDraftThumbnailDialog" @close="savedDraftThumbnailDialogCloseOutside">
            <md-dialog-content style="padding: 24px;">
                <p style="margin-bottom: 16px;" v-html="getDeletePagesConfirmContent()"></p>
                <p style="font-size: 16px; font-weight: bold;">
                    Cuelist recommends creating a backup before applying any changes.
                </p>
                <p v-if="project.autoSaveCap === true" style="margin-top: 12px;">
                    Cuelist keeps your last 10 page-change saves.
                </p>
            </md-dialog-content>

            <md-dialog-actions style="display: flex; justify-content: flex-end; padding: 16px;">
                <md-button class="md-default" @click="savedDraftThumbnailDialogClose">
                    Cancel
                </md-button>
                <md-button class="md-accent" @click="onDialogClose('proceedWithoutSaving')">
                    Proceed Without Saving
                </md-button>
                <md-button class="md-primary md-raised" @click="onDialogClose('saveAndContinue')">
                    Save & Continue
                </md-button>
            </md-dialog-actions>
        </md-dialog>
    </div>
</template>

<script>
import { escapeHtml } from '@/lib/escapeHtml';
import { mapGetters } from 'vuex';
import NavBus from '@/buses/nav';
import { pageOpDetail } from '@/lib/history-autosave';
import {PROJECT_DELETE_PAGES_URL} from '@/lib/URLs';
import { pageCountForPageOp } from '@/lib/page-labels';
import { useServerPageChange } from '@/lib/server-page-change';
import pageChangeLockMode from '@/lib/page-change-lock-mode';

const CreateProject = {
    name: 'deleteProject',
    // Which page-change lock this window uses (lib/page-change-lock-mode.js, shipped dark 20 Oct 2026).
    mixins: [pageChangeLockMode],
    props: [
        'currentPage',
        'delete_page_from',
        'delete_page_to'
    ],
    data: function() {
        return {
            posterImage: null,
            validPoster: false,
            uploadingPoster: false,
            projectCuePlacement: this.$props.cuePlacement || 'right',
            deleteErrorFromMessage: '',
            deleteErrorToMessage: '',
            pageChangeRunning: false,
            lockStaysUp: false
        };
    },
    computed: {
        ...mapGetters([
            'user',
            'users',
            'project',
            'cues',
            'notes',
            'bookmarks',
            'currentVersion',
            'projectTotalPages',
            'projectID'
        ]),
        isCreateDisabled() {
            // Check if all required fields are filled. Adjust the logic as needed for the file field.
            return !(this.project.title && this.project.file && this.project.ownerRole);
        },
        versionID: function() {
            return this.currentVersion ? this.currentVersion.id : '';
        },
        // Mirrors the helper's own refusal (routes/pdftk.js: "Refusing to delete
        // every page of the document"), so the user hears it instantly instead of
        // after a full download of the PDF.
        isEveryPageSelected() {
            const total = Number(this.totalPages) || 0;
            const from = Number(this.delete_page_from);
            const to = Number(this.delete_page_to);
            if (!total || !from || !to) {
                return false;
            }
            return Math.min(from, to) <= 1 && Math.max(from, to) >= total;
        },
        // Labels and the loaded PDF must agree before the helper rebuilds the PDF
        // (7 Oct 2026, lib/page-labels.js): a short label list used to cut pages off.
        pageOpCheck() {
            return pageCountForPageOp({ labels: this.project.pdfPageNumbers, loadedPageCount: this.projectTotalPages });
        },
        totalPages: function() {
            if (this.versionID) {
                return this.projectTotalPages || 0;
            }
            
            return (this.project && Array.isArray(this.project.pdfPageNumbers)) 
                ? this.project.pdfPageNumbers.length 
                : 0;
        }
    },
    methods: {
        deleteValidateNumber(e) {
            if (e < 1) {
                this.deleteErrorFromMessage = 'Value should be greater than or equal to 1.';
            } else if (e > this.totalPages) {
                this.deleteErrorFromMessage = `Value should be less than or equal to ${this.totalPages}.`;
            } else {
                this.deleteErrorFromMessage = '';
            }
        },
        deleteValidateToNumber(e) {
            if (e < 1) {
                this.deleteErrorToMessage = 'Value should be greater than or equal to 1.';
            } else if (e > this.totalPages) {
                this.deleteErrorToMessage = `Value should be less than or equal to ${this.totalPages}.`;
            } else {
                this.deleteErrorToMessage = '';
            }
        },
        handlePageClick(index) {
            if (this.firstSelectedIndex === null) {
                this.firstSelectedIndex = index;
                this.delete_page_from = index;
            } else {
                const start = Math.min(this.firstSelectedIndex, index);
                const end = Math.max(this.firstSelectedIndex, index);
                this.selectedIdx = [];
                for (let i = start; i <= end; i++) {
                    this.selectedIdx.push(i);
                }
                this.delete_page_to = end;
                this.firstSelectedIndex = null; // Reset after selecting the range
            }
        },
        deletePagesConfirm: function() {
            return {
                title: 'Delete Page' + (Number(this.delete_page_from) === Number(this.delete_page_to) ? '' : 's') + '?',
                ok: 'yes',
                cancel: 'Cancel'
            };
        },
        async deletePages() {
            if (this.refuseIfPagesOutOfStep()) {
                return;
            }
            // Defence in depth. The palette blocks this at the click, but the modal
            // is also reachable from its own Delete Page(s) button, and the backend
            // guard only fires after the whole PDF has been downloaded to the
            // helper. Never send a request that is certain to be refused.
            if (this.isEveryPageSelected) {
                if (this.$toast) {
                    this.$toast.error(
                        'A script must keep at least one page. Deselect a page to delete the rest.'
                    );
                }
                return;
            }
            await this.beginPageChangeWindow();
            await this.$refs.savedDraftThumbnailDialog.open();
        },
        async savedDraftThumbnailDialogClose() {
            await this.endPageChangeWindow();
            await this.$refs.savedDraftThumbnailDialog.close();
        },
        async savedDraftThumbnailDialogCloseOutside() {
            await this.endPageChangeWindow();
        },
        // Josh, 14 Oct: the lock goes on at the click, not when the window opens (lib/page-change-lock.js).
        // true = this person holds it and the change may start. 'held': someone else is changing pages and
        // their hold message shows here; anything else is an error and nothing starts.
        async lockForPageChange() {
            this.pageChangeRunning = true;
            this.lockStaysUp = false;
            this.$store.dispatch('setLoader', { type: true });
            let result;
            try {
                result = await this.$store.dispatch('takePageChangeLock');
            } catch (error) {
                console.error('[page-change] could not set the lock:', error);
                result = 'error';
            }
            if (result === 'taken') return true;
            this.pageChangeRunning = false;
            this.$store.dispatch('setLoader', { type: false });
            if (result === 'held') {
                await this.$refs.savedDraftThumbnailDialog.close();
            } else if (this.$toast) {
                // Josh's wording, 14 Oct: the lock could not be set, so nothing started.
                this.$toast.error("The page change didn't start. Nothing was changed. Please try again.");
            }
            return false;
        },
        releaseLockAfterChange() {
            this.pageChangeRunning = false;
            if (this.lockStaysUp) return Promise.resolve('kept');
            return this.$store.dispatch('releasePageChangeLock');
        },
        refuseIfPagesOutOfStep() {
            if (this.pageOpCheck.ok) {
                return false;
            }
            console.error('[page-op] refused:', this.pageOpCheck.reason);
            if (this.$toast) {
                this.$toast.error("This script's page numbering is out of step with its pages, so nothing was changed. Refresh your browser and try again. If this keeps happening, contact support.");
            }
            return true;
        },
        // The page's label ("12A"), for the History row's detail line.
        pageLabel(pageNumber) {
            const page = (this.project.pdfPageNumbers || [])[Number(pageNumber) - 1];
            return page && page.name !== undefined ? page.name : pageNumber;
        },
        getFormattedVersionTitle() {
            const now = new Date();
            const date = now.toLocaleDateString('en-GB');
            const time = now.toLocaleTimeString('en-US', { hour12: true });

            const formattedDate = date.replaceAll('/', '-');
            return `PDF Adjustment – ${formattedDate} ${time}`;
        },
        async onDialogClose(type) {
            if (type === 'proceedWithoutSaving') {
                if (!(await this.startPageChange())) return;
                await this.doDeletePages();
                await this.finishPageChange();
            } else if (type === 'saveAndContinue') {
                if (!(await this.startPageChange())) return;
                try {
                    const versionTitle = this.getFormattedVersionTitle();
                    await this.$store.dispatch('createVersion', {
                        name: versionTitle,
                        autoSave: 'page_op',
                        autoSaveDetail: pageOpDetail({ op: 'delete', from: this.pageLabel(this.delete_page_from), to: this.pageLabel(this.delete_page_to) })
                    });
                    await this.$store.dispatch('pruneAutoSaves', { kind: 'page_op' });
                    await this.doDeletePages();
                    await this.finishPageChange();
                } catch (error) {
                    console.error('Error saving version:', error);
                    this.afterFailedSave();
                    this.$store.dispatch('setLoader', { type: false });
                    this.$toast.error('Failed to save version. Please try again.');
                }
            }
        },
        async doDeletePages() {
            try {
                const pageOp = this.pageOpCheck;
                if (!pageOp.ok) throw new Error('Page op refused: ' + pageOp.reason);
                const regexp = /^https?:\/\/firebasestorage\.googleapis\.com\/v0\/b\/[^/]+\/o\/([^?]+)\?alt=media&token=[^&]+$/;
                const matches = regexp.exec(this.project.documentUrl);
                const filePath = decodeURIComponent(matches && matches[1] ? matches[1] : '');

                const deleteFrom = Number(this.delete_page_from);
                const deleteTo = Number(this.delete_page_to);
                const deleteCount = (deleteTo - deleteFrom) + 1;

                const response = await this.$http.post(PROJECT_DELETE_PAGES_URL, {
                    project_id: this.project.id,
                    documentUrl: this.project.documentUrl,
                    filePath,
                    delete_page_from: deleteFrom,
                    delete_page_to: deleteTo,
                    totalPages: pageOp.total
                });

                if (!response.body.success) throw new Error('Delete request failed');

                // Shows switched to the server renumber (lib/server-page-change.js): one call instead of the writes below.
                if (useServerPageChange(this.project)) {
                    await this.finishDeleteOnServer({ deleteFrom, deleteTo, deleteCount, pagesBefore: pageOp.total });
                    return;
                }

                this.$store.dispatch('updateShowThumbnailPageUpdate', true);
                // Update PDF Page Numbers
                if (Array.isArray(this.project.pdfPageNumbers) && this.project.pdfPageNumbers.length > 0) {
                    const updatedPdfPages = this.project.pdfPageNumbers
                        .filter((_, i) => i < deleteFrom - 1 || i > deleteTo - 1)
                        .map((item, index) => ({ ...item, id: index }));

                    await this.$store.dispatch('renamePdfPages', { pdfPage: updatedPdfPages });
                    this.$emit('thumbnailSelectionClick', []);
                }

                // Update Cues — batch the writes (was individual updateCue/deleteCue
                // per cue; ~1,000+ writes crawled on dense projects).
                const cuesToUpdate = [];
                const cuesToDelete = [];
                for (const cue of this.cues) {
                    if (cue.pageNumber >= deleteFrom && cue.pageNumber <= deleteTo) {
                        cuesToDelete.push(cue);
                    } else if (cue.pageNumber > deleteTo) {
                        cue.pageNumber -= deleteCount;
                        cuesToUpdate.push(cue);
                    }
                }

                // Update Notes
                const notePromises = [];
                for (const note of this.notes) {
                    if (note.page >= deleteFrom && note.page <= deleteTo) {
                        notePromises.push(this.$store.dispatch('removeUserNote', { note }));
                    } else if (note.page > deleteTo) {
                        note.page -= deleteCount;
                        notePromises.push(this.$store.dispatch('updateUserNote', { note }));
                    }
                }

                // Update Bookmarks — one atomic batch (see moveBookmarksBulk).
                const bookmarkMoves = [];
                const bookmarkRemoves = [];
                for (const page of Object.keys(this.bookmarks)) {
                    const pageNum = Number(page);
                    if (pageNum >= deleteFrom && pageNum <= deleteTo) {
                        bookmarkRemoves.push(page);
                    } else if (pageNum > deleteTo) {
                        bookmarkMoves.push({ from: page, to: pageNum - deleteCount });
                    }
                }

                // Wait for every persistence write before reloading, so a mid-flight
                // failure surfaces (catch below) instead of silently leaving cues,
                // notes or bookmarks on the wrong pages.
                // Act/Scene/Song markers ride along in the same barrier: delete those
                // on removed pages, shift later ones (mirrors project-settings nav).
                await Promise.all([
                    this.$store.dispatch('bulkUpdateCues', cuesToUpdate),
                    this.$store.dispatch('bulkDeleteCues', cuesToDelete),
                    ...notePromises,
                    this.$store.dispatch('moveBookmarksBulk', { moves: bookmarkMoves, removes: bookmarkRemoves }),
                    this.$store.dispatch('adjustScriptMarkersAfterPageDelete', {
                        fromPage: deleteFrom,
                        toPage: deleteTo
                    })
                ]);
                this.$store.dispatch('notifyNotesListStructureIfLinked');

                // Adjust current page and emit reload
                let newPagePosition = this.currentPage;
                const newTotalPages = Number(this.totalPages) - deleteCount;

                if (newPagePosition > deleteTo) {
                    newPagePosition -= deleteCount;
                }

                newPagePosition = Math.min(newPagePosition, newTotalPages);
                this.$emit('thumbnailSelectionClick', []);
                NavBus.$emit('reloadCurrentProject', { newPagePosition });
                this.$store.dispatch('updateShowThumbnailPageUpdate', false);
            } catch (error) {
                console.error(error);
                // A server change that stopped part way after every retry keeps the lock up (Josh, 14 Oct).
                if (error && error.stoppedPartWay) this.lockStaysUp = true;
                this.$store.dispatch('updateShowThumbnailPageUpdate', false);
                this.$store.dispatch('setLoader', { type: false });
                if (this.$toast) this.$toast.error('Something went wrong updating the pages. Please reload and check the affected pages.');
            }
        },
        async finishDeleteOnServer({ deleteFrom, deleteTo, deleteCount, pagesBefore }) {
            await this.$store.dispatch('applyPageChangeOnServer', {
                op: 'delete',
                params: { from: deleteFrom, to: deleteTo },
                pagesBefore
            });
            // Only now (14 Oct 2026): the flag reloads this tab, and a reload while the server
            // was still renumbering saw the new PDF with the old labels and "repaired" them.
            this.$store.dispatch('updateShowThumbnailPageUpdate', true);
            let newPagePosition = this.currentPage;
            if (newPagePosition > deleteTo) {
                newPagePosition -= deleteCount;
            }
            newPagePosition = Math.min(newPagePosition, Number(this.totalPages) - deleteCount);
            this.$emit('thumbnailSelectionClick', []);
            NavBus.$emit('reloadCurrentProject', { newPagePosition });
            this.$store.dispatch('updateShowThumbnailPageUpdate', false);
        },
        getDeletePagesConfirmContent() {
            // Page labels are typed by anyone who can edit the show, and this text goes out through v-html: escape them
            // (a label holding markup would otherwise run in whoever opens Delete Page; found 29 Oct 2026).
            const fromNumber = this.delete_page_from && escapeHtml(this.project.pdfPageNumbers[this.delete_page_from - 1].name);
            const toNumber = this.delete_page_to && escapeHtml(this.project.pdfPageNumbers[this.delete_page_to - 1].name);
            if (Number(this.delete_page_from) === Number(this.delete_page_to)) {
                return `Warning, you are about to delete page <strong>${fromNumber}</strong>. All cues and text notes on this page will also be deleted. This cannot be undone.`;
            }
            return `Warning, you are about to delete pages <strong>${fromNumber}</strong> to <strong>${toNumber}</strong>. All cues and text notes on these pages will also be deleted. This cannot be undone.`;
        },
        closeDialog() {
            this.$refs.deletePage.close();
        },
        fixContent() {
            if (this.$refs.stepper) {
                this.$nextTick(() => {
                    this.$refs.stepper.calculateStepsWidthAndPosition();
                });
            }
        },
        startOver() {
            
        },
        cancel() {
            this.$refs.deletePage.close();
        }
    },
    components: {
    }
};
export default CreateProject;
</script>

<style>
.md-dialog {
    min-width: 400px;
}
</style>


// WEBPACK FOOTER //
// delete-page.vue?699aceb6